import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Card, Button, Form, Alert, Badge, ButtonGroup, Spinner } from "react-bootstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { fetchMapperData, replaceCatalog, upsertMapping, clearAllMappings, deleteItem as deleteItemApi } from "../services/upcMapping.service";
import SwipeableCard from "./SwipeableCard.component";

const STORAGE_KEY = "shoolin.cokeMapper.v1";
const MAPPING_TYPE = "coke";

function loadState() {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (v && Array.isArray(v.items)) return v;
  } catch {}
  return { fileName: "", importedAt: 0, items: [] };
}
function saveState(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
}

// Map an xlsx row to our internal item shape.
function rowToItem(row, existingByMat) {
  const mat = String(row["MAT#"] ?? row["MAT"] ?? "").trim();
  const itemUpc = String(row["UPC"] ?? "").trim();
  const existing = existingByMat.get(mat);
  return {
    mat,
    name: String(row["Product Name"] ?? "").trim(),
    category: String(row["Category"] ?? "").trim(),
    packCode: String(row["Pack Code"] ?? "").trim(),
    unitsPerCase: Number(row["Units/Case"] ?? 0) || 0,
    itemUpc,
    caseUpc: existing?.caseUpc || "",
    scannedAt: existing?.scannedAt || 0,
  };
}

function ItemCard({ item, isScanning, onStartScan, onStopScan, onScanned, onClear, onManualEdit, onDelete }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const lastResultRef = useRef({ text: "", at: 0 });
  const [scanError, setScanError] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.caseUpc);

  useEffect(() => {
    if (!isScanning) return;
    let cancelled = false;
    setScanError("");
    const reader = new BrowserMultiFormatReader();
    (async () => {
      try {
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: "environment" } }, audio: false },
          videoRef.current,
          (result) => {
            if (cancelled || !result) return;
            const text = result.getText();
            const now = Date.now();
            if (text === lastResultRef.current.text && now - lastResultRef.current.at < 1500) return;
            lastResultRef.current = { text, at: now };
            if (navigator.vibrate) navigator.vibrate(40);
            onScanned(text);
          }
        );
        if (cancelled) controls.stop();
        else controlsRef.current = controls;
      } catch (e) {
        const name = e?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError") {
          setScanError("Camera permission denied.");
        } else if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
          setScanError("Camera requires HTTPS.");
        } else {
          setScanError(e?.message || "Could not start camera.");
        }
      }
    })();
    return () => {
      cancelled = true;
      try { controlsRef.current?.stop(); } catch {}
      controlsRef.current = null;
    };
  }, [isScanning, onScanned]);

  const isScanned = !!item.caseUpc;

  const saveEdit = () => {
    onManualEdit(draft.trim());
    setEditing(false);
  };

  return (
    <Card className={`mb-3 ${isScanned ? "border-success" : ""}`}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h5 className="mb-1" style={{ wordBreak: "break-word" }}>{item.name || "(no name)"}</h5>
            <div className="text-muted small">
              MAT# {item.mat || "—"} · {item.packCode || "—"} · {item.unitsPerCase || "?"} per case
            </div>
            {item.category && <div className="text-muted small">{item.category}</div>}
          </div>
          <div className="d-flex align-items-center gap-2 ms-2">
            <Badge bg={isScanned ? "success" : "secondary"}>
              {isScanned ? "Scanned" : "Unscanned"}
            </Badge>
            <Button
              variant="link"
              size="sm"
              className="p-0 text-danger"
              style={{ fontSize: 18, lineHeight: 1, textDecoration: "none" }}
              onClick={() => {
                if (window.confirm(`Delete "${item.name || item.mat}" from the mapper?`)) {
                  onDelete?.();
                }
              }}
              aria-label="Delete this item"
              title="Delete this item"
            >
              ×
            </Button>
          </div>
        </div>

        <div className="row g-2 mb-2">
          <div className="col-12 col-md-6">
            <div className="text-muted small">Item UPC</div>
            <div style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
              {item.itemUpc || <span className="text-muted">—</span>}
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div className="text-muted small">Case UPC</div>
            {editing ? (
              <div className="d-flex gap-2">
                <Form.Control
                  size="sm"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Enter case UPC"
                  autoFocus
                />
                <Button size="sm" variant="primary" onClick={saveEdit}>Save</Button>
                <Button size="sm" variant="outline-secondary" onClick={() => { setEditing(false); setDraft(item.caseUpc); }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                {item.caseUpc || <span className="text-muted">Not scanned</span>}
              </div>
            )}
          </div>
        </div>

        {isScanning && (
          <div
            style={{
              position: "relative",
              background: "#000",
              borderRadius: 8,
              overflow: "hidden",
              aspectRatio: "16 / 9",
              marginTop: 8,
            }}
          >
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "min(78%, 320px)",
                height: "min(40%, 180px)",
                border: "2px solid rgba(255,255,255,0.9)",
                borderRadius: 12,
                boxShadow: "0 0 0 9999px rgba(0,0,0,0.32)",
                pointerEvents: "none",
              }}
            />
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 8, textAlign: "center", color: "#fff", fontSize: 14, textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}>
              Point at the case barcode
            </div>
          </div>
        )}

        {scanError && (
          <Alert variant="danger" className="mt-2 mb-0 py-2 small">{scanError}</Alert>
        )}

        <div className="d-flex flex-wrap gap-2 mt-3">
          {isScanning ? (
            <Button variant="outline-danger" size="sm" onClick={onStopScan}>Stop camera</Button>
          ) : (
            <Button variant={isScanned ? "outline-primary" : "primary"} size="sm" onClick={onStartScan}>
              📷 {isScanned ? "Rescan" : "Scan case UPC"}
            </Button>
          )}
          {!editing && (
            <Button variant="outline-secondary" size="sm" onClick={() => { setDraft(item.caseUpc); setEditing(true); }}>
              {isScanned ? "Edit" : "Type manually"}
            </Button>
          )}
          {isScanned && !editing && (
            <Button variant="outline-secondary" size="sm" onClick={onClear}>Clear</Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

function CokeMapper() {
  const [state, setState] = useState(loadState);
  const [filter, setFilter] = useState("all"); // all | scanned | unscanned
  const [search, setSearch] = useState("");
  const [scanningMat, setScanningMat] = useState(null);
  const [importError, setImportError] = useState("");
  const [syncError, setSyncError] = useState("");
  const [syncing, setSyncing] = useState(true);

  const items = state.items;

  // On mount, pull catalog + mappings from the server. Server is source of truth.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { catalog, mappings } = await fetchMapperData(MAPPING_TYPE);
        if (cancelled) return;
        if (!catalog) {
          // No server catalog yet — keep any local cache untouched (so a fresh
          // upload before sync still survives), but mark sync as done.
          return;
        }
        const byId = new Map(mappings.map((m) => [m.externalId, m]));
        const items = catalog.items.map((it) => {
          const m = byId.get(it.mat);
          return m
            ? { ...it, caseUpc: m.scannedUpc, scannedAt: new Date(m.scannedAt).getTime() }
            : { ...it, caseUpc: "", scannedAt: 0 };
        });
        const merged = {
          fileName: catalog.fileName,
          importedAt: new Date(catalog.importedAt).getTime(),
          items,
        };
        setState(merged);
        saveState(merged);
      } catch (err) {
        if (!cancelled) setSyncError(err?.response?.data?.message || "Could not load saved data.");
      } finally {
        if (!cancelled) setSyncing(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => {
    const scanned = items.filter((i) => i.caseUpc).length;
    return { total: items.length, scanned, unscanned: items.length - scanned };
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (filter === "scanned" && !i.caseUpc) return false;
      if (filter === "unscanned" && i.caseUpc) return false;
      if (!q) return true;
      return (
        i.name.toLowerCase().includes(q) ||
        i.mat.toLowerCase().includes(q) ||
        i.itemUpc.toLowerCase().includes(q) ||
        i.caseUpc.toLowerCase().includes(q) ||
        i.packCode.toLowerCase().includes(q)
      );
    });
  }, [items, filter, search]);

  const persist = (next) => {
    setState(next);
    saveState(next);
  };

  const handleFile = (file) => {
    setImportError("");
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!rows.length) throw new Error("Sheet is empty.");
        const existingByMat = new Map(items.map((i) => [i.mat, i]));
        const next = rows.map((r) => rowToItem(r, existingByMat)).filter((i) => i.mat || i.name);

        // Local first (instant feedback), then push to server.
        persist({ fileName: file.name, importedAt: Date.now(), items: next });

        try {
          // Server stores catalog without per-item scan state; scans live in mappings.
          const itemsForServer = next.map(({ caseUpc, scannedAt, ...rest }) => rest);
          await replaceCatalog(MAPPING_TYPE, file.name, itemsForServer);
          setSyncError("");
        } catch (err) {
          setSyncError(err?.response?.data?.message || "Saved locally, but could not sync catalog to server.");
        }
      } catch (err) {
        setImportError(err.message || "Failed to read Excel file.");
      }
    };
    reader.onerror = () => setImportError("Could not read file.");
    reader.readAsArrayBuffer(file);
  };

  const setCaseUpc = async (mat, caseUpc) => {
    const item = state.items.find((i) => i.mat === mat);
    const prevValue = item?.caseUpc || "";
    const prevTs = item?.scannedAt || 0;

    // Optimistic local update
    persist({
      ...state,
      items: state.items.map((i) =>
        i.mat === mat ? { ...i, caseUpc, scannedAt: caseUpc ? Date.now() : 0 } : i
      ),
    });

    try {
      await upsertMapping(MAPPING_TYPE, mat, caseUpc, item?.name || "");
      setSyncError("");
    } catch (err) {
      // Revert on failure
      setSyncError(err?.response?.data?.message || "Could not save to server. Change reverted.");
      setState((s) => {
        const reverted = {
          ...s,
          items: s.items.map((i) =>
            i.mat === mat ? { ...i, caseUpc: prevValue, scannedAt: prevTs } : i
          ),
        };
        saveState(reverted);
        return reverted;
      });
    }
  };

  const onScanned = (mat) => (text) => {
    setCaseUpc(mat, text);
    setScanningMat(null); // close camera after a successful scan
  };

  const deleteItem = async (mat) => {
    if (scanningMat === mat) setScanningMat(null);
    const prev = state;
    persist({ ...state, items: state.items.filter((i) => i.mat !== mat) });
    try {
      await deleteItemApi(MAPPING_TYPE, mat);
      setSyncError("");
    } catch (err) {
      setSyncError(err?.response?.data?.message || "Could not delete on server. Reverted.");
      setState(prev);
      saveState(prev);
    }
  };

  const moveToBottom = async (mat) => {
    const idx = state.items.findIndex((i) => i.mat === mat);
    if (idx < 0) return;
    const moved = state.items[idx];
    const reordered = [...state.items.slice(0, idx), ...state.items.slice(idx + 1), moved];
    const next = { ...state, items: reordered };
    persist(next);
    try {
      const itemsForServer = reordered.map(({ caseUpc, scannedAt, ...rest }) => rest);
      await replaceCatalog(MAPPING_TYPE, state.fileName || "", itemsForServer);
      setSyncError("");
    } catch (err) {
      setSyncError(err?.response?.data?.message || "Reorder saved locally but did not sync.");
    }
  };

  const exportXlsx = () => {
    const rows = items.map((i) => ({
      "MAT#": i.mat,
      "Product Name": i.name,
      "Category": i.category,
      "Pack Code": i.packCode,
      "Units/Case": i.unitsPerCase,
      "Item UPC": i.itemUpc,
      "Case UPC": i.caseUpc,
      "Scanned At": i.scannedAt ? new Date(i.scannedAt).toISOString() : "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Coke Mapping");
    const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(new Blob([out], { type: "application/octet-stream" }), "coke-mapping.xlsx");
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all imported items and scanned UPCs (this also clears them on the server)?")) return;
    try {
      await clearAllMappings(MAPPING_TYPE);
      persist({ fileName: "", importedAt: 0, items: [] });
      setScanningMat(null);
      setSyncError("");
    } catch (err) {
      setSyncError(err?.response?.data?.message || "Could not clear server data.");
    }
  };

  return (
    <Container className="invoice-upload-container mt-4">
      <h2 className="mb-1">Coke Mapper</h2>
      <p className="text-muted">Scan the case barcode for each product to map case UPC → item UPC. Saved to the server per store.</p>

      {syncing && (
        <div className="text-muted small mb-2">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading saved mappings…
        </div>
      )}
      {syncError && (
        <Alert variant="warning" className="py-2 small" dismissible onClose={() => setSyncError("")}>
          {syncError}
        </Alert>
      )}

      <Card className="p-3 mb-3">
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
          <div>
            <Form.Label className="mb-1 small text-muted">Upload Excel</Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx,.xls"
              size="sm"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              style={{ maxWidth: 380 }}
            />
            {state.fileName && (
              <div className="small text-muted mt-1">
                Loaded <strong>{state.fileName}</strong> · {counts.total} items · imported {new Date(state.importedAt).toLocaleString()}
              </div>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" size="sm" disabled={!items.length} onClick={exportXlsx}>
              Export .xlsx
            </Button>
            <Button variant="outline-danger" size="sm" disabled={!items.length} onClick={clearAll}>
              Clear all
            </Button>
          </div>
        </div>

        {importError && (
          <Alert variant="danger" className="mb-0 mt-3 py-2 small" dismissible onClose={() => setImportError("")}>
            {importError}
          </Alert>
        )}
      </Card>

      {items.length > 0 && (
        <>
          <Card className="p-3 mb-3" style={{ position: "sticky", top: 80, zIndex: 5 }}>
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
              <ButtonGroup size="sm">
                <Button
                  variant={filter === "all" ? "primary" : "outline-primary"}
                  onClick={() => setFilter("all")}
                >
                  All <Badge bg="light" text="dark" className="ms-1">{counts.total}</Badge>
                </Button>
                <Button
                  variant={filter === "unscanned" ? "primary" : "outline-primary"}
                  onClick={() => setFilter("unscanned")}
                >
                  Unscanned <Badge bg="light" text="dark" className="ms-1">{counts.unscanned}</Badge>
                </Button>
                <Button
                  variant={filter === "scanned" ? "primary" : "outline-primary"}
                  onClick={() => setFilter("scanned")}
                >
                  Scanned <Badge bg="light" text="dark" className="ms-1">{counts.scanned}</Badge>
                </Button>
              </ButtonGroup>

              <Form.Control
                type="text"
                size="sm"
                placeholder="Search name, MAT#, UPC, pack code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ maxWidth: 320 }}
              />
            </div>
          </Card>

          {filtered.length === 0 ? (
            <div className="text-muted text-center py-4">No items match the current filter.</div>
          ) : (
            filtered.map((item) => (
              <SwipeableCard
                key={item.mat}
                disabled={scanningMat === item.mat}
                onSwipeAway={() => moveToBottom(item.mat)}
              >
                <ItemCard
                  item={item}
                  isScanning={scanningMat === item.mat}
                  onStartScan={() => setScanningMat(item.mat)}
                  onStopScan={() => setScanningMat(null)}
                  onScanned={onScanned(item.mat)}
                  onClear={() => setCaseUpc(item.mat, "")}
                  onManualEdit={(text) => setCaseUpc(item.mat, text)}
                  onDelete={() => deleteItem(item.mat)}
                />
              </SwipeableCard>
            ))
          )}
        </>
      )}

      {items.length === 0 && !importError && (
        <div className="text-muted text-center py-5">
          Upload a Coke invoice Excel to get started.
        </div>
      )}
    </Container>
  );
}

export default CokeMapper;
